import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  DownloadIcon,
  EllipsisVerticalIcon,
  Redo2Icon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import { Button as RACButton } from "react-aria-components";

import { AccessibleIcon } from "@/components/accessible-icon";
import { FullPageError } from "@/components/fullpage-error";
import { Menu, MenuItem, MenuItemLabel, MenuTrigger } from "@/components/menu";
import { Popover } from "@/components/popover";
import { UnlockModal } from "@/components/upgrade-modals/unlock-modal";
import { FeatureGuard } from "@/features/auth/permissions/feature-guard";
import { useUser } from "@/hooks/useUser";
import { isKickstarterAccount } from "@/utils/isKickstarterAccount";

import { getFileConfigPayload } from "../get-file-config-payload";
import { getFileTokenConfig } from "../get-file-token-config";
import { useDownloadFile } from "../hooks/useDownloadFile";
import { useDownloadFolder } from "../hooks/useDownloadFolder";
import { useRestoreFilesMutation } from "../hooks/useRestoreFilesMutation";
import { useDeleteFilesDialogState } from "./file-delete-dialog";
import { useRestoreFilesDialogState } from "./file-restored-dialog";

type ActionMenuProps = {
  handleResetRowSelection?: () => void;
  href: string | undefined;
  isDirectory: boolean;
  itemId: "graphic" | "unknown" | "video" | "website" | number;
};

export function ActionMenu({
  handleResetRowSelection,
  href,
  isDirectory,
  itemId,
}: ActionMenuProps) {
  const { data: userData } = useUser();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const { toggle: toggleRestoreFilesDialog } = useRestoreFilesDialogState();
  const { setSelectedItemsIds, toggle: toggleDeleteFilesDialog } =
    useDeleteFilesDialogState();
  const navigate = useNavigate();
  const { brandId, folder, projectId, requestId, requestType } = useSearch({
    strict: false,
  });
  const fileConfigPayload = getFileConfigPayload({
    brandId,
    folder,
    href,
    itemId,
    projectId,
    requestId,
    requestType,
  });

  const configUrl = getFileTokenConfig(fileConfigPayload);

  const downloadFileMutation = useDownloadFile();
  const { error, isError, mutate: downloadFolder, reset } = useDownloadFolder();

  const restoreFromTrashMutation = useRestoreFilesMutation({
    onSuccess: () => {
      handleResetRowSelection?.();
      toggleRestoreFilesDialog();
    },
  });

  const isKickstarter = isKickstarterAccount(
    userData?.user.accountType ?? null,
  );
  const isTrashFolder = folder === "trash";

  const handleDownload = () => {
    if (isKickstarter) {
      setIsUpgradeModalOpen(true);

      return;
    }

    if (isDirectory && configUrl) {
      downloadFolder(configUrl);
    } else if (typeof itemId === "number") {
      downloadFileMutation.mutate(itemId);
    }
  };

  const handleUpgrade = () => {
    navigate({ to: "/pricing" });
  };

  return (
    <>
      {isError && (
        <FullPageError onCancel={reset}>
          <div className="flex flex-col items-center justify-center gap-2">
            <p className="text-sm text-text-secondary font-bold">
              {error.message || "Download failed"}
            </p>
            <p className="text-sm text-text-secondary">
              Please cancel and try again
            </p>
          </div>
        </FullPageError>
      )}
      <MenuTrigger>
        <RACButton aria-label="Menu" className="ml-auto p-xs outline-hidden">
          <EllipsisVerticalIcon className="text-button-tertiary-fg" size={13} />
        </RACButton>
        <Popover>
          <Menu className="w-[192px]">
            <MenuItem
              isDisabled={downloadFileMutation.isPending}
              onAction={handleDownload}
            >
              <AccessibleIcon>
                <DownloadIcon />
              </AccessibleIcon>
              <MenuItemLabel>Download</MenuItemLabel>
            </MenuItem>
            {!isDirectory && !isTrashFolder && (
              <FeatureGuard feature="my-files.files.delete">
                <MenuItem
                  destructive
                  onAction={() => {
                    setSelectedItemsIds([itemId as number]);
                    toggleDeleteFilesDialog();
                  }}
                >
                  <AccessibleIcon>
                    <Trash2Icon />
                  </AccessibleIcon>
                  <MenuItemLabel>Delete</MenuItemLabel>
                </MenuItem>
              </FeatureGuard>
            )}
            {isTrashFolder && (
              <MenuItem
                onAction={() => {
                  if (typeof itemId === "number") {
                    restoreFromTrashMutation.mutate([itemId]);
                  }
                }}
              >
                <AccessibleIcon>
                  <Redo2Icon />
                </AccessibleIcon>
                <MenuItemLabel>Restore</MenuItemLabel>
              </MenuItem>
            )}
          </Menu>
        </Popover>
      </MenuTrigger>

      {/* Kickstarter Unlock Modal */}
      <UnlockModal
        isOpen={isUpgradeModalOpen}
        onOpenChange={setIsUpgradeModalOpen}
        onUpgrade={handleUpgrade}
      />
    </>
  );
}
